import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { InputHora } from './ui/input-hora';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  Save, Loader2, AlertTriangle, CheckCircle, 
  ArrowRight, Calendar, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Formatar data curta (Seg, 21/07)
const formatDateShort = (dataISO) => {
  if (!dataISO) return '';
  const dias = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const [ano, mes, dia] = dataISO.split('-');
  const date = new Date(ano, mes - 1, dia);
  return `${dias[date.getDay()]}, ${dia}/${mes}`;
};

// Formatar data completa
const formatDateFull = (dataISO) => {
  if (!dataISO) return '';
  const dias = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  const [ano, mes, dia] = dataISO.split('-');
  const date = new Date(ano, mes - 1, dia);
  return `${dias[date.getDay()]}, ${dia}/${mes}/${ano}`;
};

const ModalAjusteDia = ({ 
  open, 
  onClose, 
  empregado, 
  data, 
  marcacoesAtuais, 
  escala,
  token,
  empresaId,
  onSave 
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Refs para navegação entre campos
  const inputRefs = useRef({});
  
  // Horários do dia (E1, S1, E2, S2, E3, S3)
  const [horarios, setHorarios] = useState({
    entrada1: '',
    saida1: '',
    entrada2: '',
    saida2: '',
    entrada3: '',
    saida3: ''
  });
  
  // Horários originais para comparação
  const [horariosOriginais, setHorariosOriginais] = useState({});
  
  // Motivo do ajuste
  const [motivo, setMotivo] = useState('');
  
  // Reprocessar jornada
  const [reprocessar, setReprocessar] = useState(true);
  
  // Estado do modal de propagação
  const [showPropagacao, setShowPropagacao] = useState(false);
  const [diasPropagacao, setDiasPropagacao] = useState([]);
  const [diasSelecionados, setDiasSelecionados] = useState({});
  const [loadingPropagacao, setLoadingPropagacao] = useState(false);
  
  // Carregar marcações do dia quando abrir
  useEffect(() => {
    if (open && empregado && data) {
      carregarMarcacoes();
    }
  }, [open, empregado?.id, data]);
  
  const carregarMarcacoes = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/marcacoes?empregado_id=${empregado.id}&data=${data}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const marcacoes = await res.json();
        
        // Ordenar por hora e mapear para slots
        const sorted = marcacoes
          .filter(m => m.status !== 'excluida')
          .sort((a, b) => (a.hora || '').localeCompare(b.hora || ''));
        
        const slots = {
          entrada1: sorted[0]?.hora || '',
          saida1: sorted[1]?.hora || '',
          entrada2: sorted[2]?.hora || '',
          saida2: sorted[3]?.hora || '',
          entrada3: sorted[4]?.hora || '',
          saida3: sorted[5]?.hora || ''
        };
        
        setHorarios(slots);
        setHorariosOriginais({ ...slots });
      } else {
        const error = await res.json().catch(() => ({}));
        console.error('Erro ao carregar marcações:', error);
        toast.error(error.detail || 'Erro ao carregar marcações do dia');
      }
    } catch (error) {
      console.error('Erro ao carregar marcações:', error);
      toast.error('Erro de conexão ao carregar marcações');
    } finally {
      setLoading(false);
    }
  };
  
  // Verificar se houve alteração em algum horário
  const getAlteracoes = () => {
    const alteracoes = [];
    const slots = ['entrada1', 'saida1', 'entrada2', 'saida2', 'entrada3', 'saida3'];
    const labels = ['Entrada 1', 'Saída 1', 'Entrada 2', 'Saída 2', 'Entrada 3', 'Saída 3'];
    
    slots.forEach((slot, idx) => {
      const original = horariosOriginais[slot] || '';
      const novo = horarios[slot] || '';
      
      if (original !== novo) {
        alteracoes.push({
          slot,
          label: labels[idx],
          original: original || '(vazio)',
          novo: novo || '(vazio)',
          diff: calcularDiff(original, novo)
        });
      }
    });
    
    return alteracoes;
  };
  
  // Calcular diferença em minutos
  const calcularDiff = (horaOriginal, horaNova) => {
    if (!horaOriginal && horaNova) return 'adicionado';
    if (horaOriginal && !horaNova) return 'removido';
    if (!horaOriginal || !horaNova) return '';
    
    const [h1, m1] = horaOriginal.split(':').map(Number);
    const [h2, m2] = horaNova.split(':').map(Number);
    
    const min1 = h1 * 60 + m1;
    const min2 = h2 * 60 + m2;
    const diff = min2 - min1;
    
    if (diff === 0) return '';
    return diff > 0 ? `+${diff} min` : `${diff} min`;
  };
  
  // Salvar ajustes
  const handleSalvar = async () => {
    const alteracoes = getAlteracoes();
    
    if (alteracoes.length === 0) {
      toast.info('Nenhuma alteração realizada');
      onClose();
      return;
    }
    
    if (motivo.trim().length < 5) {
      toast.error('Informe o motivo do ajuste (mínimo 5 caracteres)');
      return;
    }
    
    setSaving(true);
    try {
      // Usar endpoint de digitação rápida que já suporta múltiplos slots
      const res = await fetch(`${API_URL}/api/marcacoes/digitacao-rapida`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          empregado_id: empregado.id,
          dias: [{
            data: data,
            entrada1: horarios.entrada1 || null,
            saida1: horarios.saida1 || null,
            entrada2: horarios.entrada2 || null,
            saida2: horarios.saida2 || null,
            entrada3: horarios.entrada3 || null,
            saida3: horarios.saida3 || null
          }],
          justificativa_tipo: 'ajuste_manual',
          justificativa_motivo: motivo,
          ja_aprovado: true
        })
      });
      
      if (res.ok) {
        toast.success('Ajustes salvos com sucesso');
        
        // Perguntar sobre propagação
        if (alteracoes.length > 0) {
          buscarDiasPropagacao(alteracoes);
        } else {
          onSave && onSave();
          onClose();
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao salvar ajustes');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar ajustes');
    } finally {
      setSaving(false);
    }
  };
  
  // Buscar dias seguintes para propagação
  const buscarDiasPropagacao = async (alteracoes) => {
    setLoadingPropagacao(true);
    try {
      // Buscar marcações dos próximos 30 dias
      const dataInicio = data;
      const dataObj = new Date(data);
      dataObj.setDate(dataObj.getDate() + 30);
      const dataFim = dataObj.toISOString().split('T')[0];
      
      const res = await fetch(
        `${API_URL}/api/marcacoes/periodo?empresa_id=${empresaId}&empregado_id=${empregado.id}&data_inicio=${dataInicio}&data_fim=${dataFim}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const response = await res.json();
        const dados = response.marcacoes || response || [];
        
        // Filtrar dias com marcações similares (excluindo o dia atual)
        const diasComMarcacoes = dados
          .filter(d => d.data !== data && (d.entrada1 || d.saida1))
          .map(d => {
            // Calcular horários propostos baseado nas alterações
            const propostos = { ...d };
            alteracoes.forEach(alt => {
              if (d[alt.slot] && alt.original !== '(vazio)') {
                // Aplicar mesma diferença
                const [h1, m1] = (d[alt.slot] || '00:00').split(':').map(Number);
                const [h2, m2] = (alt.novo !== '(vazio)' ? alt.novo : '00:00').split(':').map(Number);
                const [h3, m3] = (alt.original !== '(vazio)' ? alt.original : '00:00').split(':').map(Number);
                
                // Diferença aplicada
                const diffMin = (h2 * 60 + m2) - (h3 * 60 + m3);
                const novoMin = (h1 * 60 + m1) + diffMin;
                const novaHora = Math.floor(novoMin / 60);
                const novoMinuto = novoMin % 60;
                
                if (novoMin >= 0 && novaHora < 24) {
                  propostos[`${alt.slot}_proposto`] = 
                    `${novaHora.toString().padStart(2, '0')}:${novoMinuto.toString().padStart(2, '0')}`;
                }
              } else if (alt.original === '(vazio)' && alt.novo !== '(vazio)') {
                // Adicionar novo horário
                propostos[`${alt.slot}_proposto`] = alt.novo;
              }
            });
            
            return {
              ...d,
              ...propostos,
              selecionado: true
            };
          })
          .slice(0, 15); // Limitar a 15 dias
        
        if (diasComMarcacoes.length > 0) {
          setDiasPropagacao(diasComMarcacoes);
          const selecao = {};
          diasComMarcacoes.forEach(d => { selecao[d.data] = true; });
          setDiasSelecionados(selecao);
          setShowPropagacao(true);
        } else {
          onSave && onSave();
          onClose();
        }
      }
    } catch (error) {
      console.error('Erro ao buscar dias:', error);
      onSave && onSave();
      onClose();
    } finally {
      setLoadingPropagacao(false);
    }
  };
  
  // Aplicar propagação
  const handlePropagar = async () => {
    const diasParaPropagar = diasPropagacao.filter(d => diasSelecionados[d.data]);
    
    if (diasParaPropagar.length === 0) {
      setShowPropagacao(false);
      onSave && onSave();
      onClose();
      return;
    }
    
    setSaving(true);
    try {
      // Preparar dados para digitação rápida em lote
      const diasFormatados = diasParaPropagar.map(d => ({
        data: d.data,
        entrada1: d.entrada1_proposto || d.entrada1 || null,
        saida1: d.saida1_proposto || d.saida1 || null,
        entrada2: d.entrada2_proposto || d.entrada2 || null,
        saida2: d.saida2_proposto || d.saida2 || null,
        entrada3: d.entrada3_proposto || d.entrada3 || null,
        saida3: d.saida3_proposto || d.saida3 || null
      }));
      
      const res = await fetch(`${API_URL}/api/marcacoes/digitacao-rapida`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          empregado_id: empregado.id,
          dias: diasFormatados,
          justificativa_tipo: 'ajuste_manual',
          justificativa_motivo: `${motivo} (propagação de ${formatDateShort(data)})`,
          ja_aprovado: true
        })
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Ajustes aplicados em ${result.estatisticas?.dias_processados || diasParaPropagar.length} dias`);
      } else {
        toast.error('Erro ao propagar ajustes');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao propagar ajustes');
    } finally {
      setSaving(false);
      setShowPropagacao(false);
      onSave && onSave();
      onClose();
    }
  };
  
  const alteracoes = getAlteracoes();
  const temAlteracoes = alteracoes.length > 0;
  
  // Slots para exibição
  const slots = [
    { key: 'entrada1', label: 'Entrada 1' },
    { key: 'saida1', label: 'Saída 1' },
    { key: 'entrada2', label: 'Entrada 2' },
    { key: 'saida2', label: 'Saída 2' },
    { key: 'entrada3', label: 'Entrada 3' },
    { key: 'saida3', label: 'Saída 3' }
  ];
  
  // Modal de propagação
  if (showPropagacao) {
    const totalSelecionados = Object.values(diasSelecionados).filter(Boolean).length;
    
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Propagar Ajustes para Dias Seguintes
            </DialogTitle>
            <DialogDescription>
              Deseja aplicar as mesmas correções nos dias seguintes com marcações similares?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Resumo do ajuste */}
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-2">Ajustes realizados em {formatDateShort(data)}:</p>
              <div className="space-y-1">
                {alteracoes.map((alt, idx) => (
                  <div key={idx} className="text-sm flex items-center gap-2">
                    <span className="text-slate-600">{alt.label}:</span>
                    <span className="font-mono text-slate-500">{alt.original}</span>
                    <ArrowRight className="w-3 h-3 text-slate-400" />
                    <span className="font-mono font-medium">{alt.novo}</span>
                    <Badge variant="outline" className="text-xs">{alt.diff}</Badge>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Tabela de dias */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={totalSelecionados === diasPropagacao.length}
                        onCheckedChange={(checked) => {
                          const novaSel = {};
                          diasPropagacao.forEach(d => { novaSel[d.data] = checked; });
                          setDiasSelecionados(novaSel);
                        }}
                      />
                    </TableHead>
                    <TableHead className="w-24">Data</TableHead>
                    <TableHead>Horários Atuais</TableHead>
                    <TableHead>Horários Propostos</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {diasPropagacao.map((dia) => (
                    <TableRow key={dia.data} className={diasSelecionados[dia.data] ? 'bg-blue-50/50' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={diasSelecionados[dia.data] || false}
                          onCheckedChange={(checked) => {
                            setDiasSelecionados(prev => ({ ...prev, [dia.data]: checked }));
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-sm">
                        {formatDateShort(dia.data)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2 font-mono text-xs text-slate-500">
                          {dia.entrada1 || '--'} | {dia.saida1 || '--'} | {dia.entrada2 || '--'} | {dia.saida2 || '--'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2 font-mono text-xs font-medium">
                          {dia.entrada1_proposto || dia.entrada1 || '--'} | 
                          {dia.saida1_proposto || dia.saida1 || '--'} | 
                          {dia.entrada2_proposto || dia.entrada2 || '--'} | 
                          {dia.saida2_proposto || dia.saida2 || '--'}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>{totalSelecionados} dias selecionados</span>
              <span>{totalSelecionados * 4} marcações serão ajustadas</span>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPropagacao(false);
                onSave && onSave();
                onClose();
              }}
            >
              Não, apenas este dia
            </Button>
            <Button 
              onClick={handlePropagar}
              disabled={saving || totalSelecionados === 0}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Aplicar em {totalSelecionados} dias
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Ajuste de Marcações
          </DialogTitle>
          <DialogDescription>
            {empregado?.nome} - {formatDateFull(data)}
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Escala prevista */}
            {escala && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <span className="text-slate-500">Escala prevista: </span>
                <span className="font-medium">
                  {escala.hora_inicio || '08:00'} às {escala.hora_fim || '17:00'}
                  {escala.intervalo && ` (${escala.intervalo}h intervalo)`}
                </span>
              </div>
            )}
            
            {/* Grid de horários */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Marcações do Dia</Label>
              <div className="grid grid-cols-6 gap-3">
                {slots.map(({ key, label }, idx) => {
                  const original = horariosOriginais[key] || '';
                  const atual = horarios[key] || '';
                  const alterado = original !== atual;
                  const slotKeys = slots.map(s => s.key);
                  
                  const navigateToNext = () => {
                    if (idx < slots.length - 1) {
                      inputRefs.current[slots[idx + 1].key]?.focus();
                    }
                  };
                  
                  const navigateToPrev = () => {
                    if (idx > 0) {
                      inputRefs.current[slots[idx - 1].key]?.focus();
                    }
                  };
                  
                  return (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs text-slate-500">{label}</Label>
                      {original && (
                        <p className="text-xs text-slate-400 font-mono">
                          {original}
                        </p>
                      )}
                      <InputHora
                        ref={el => inputRefs.current[key] = el}
                        value={atual}
                        onChange={(e) => setHorarios(prev => ({ ...prev, [key]: e.target.value }))}
                        onEnter={navigateToNext}
                        onBackEmpty={navigateToPrev}
                        className={`h-10 ${alterado ? 'border-amber-400 bg-amber-50' : ''}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Resumo das alterações */}
            {temAlteracoes && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Alterações pendentes:
                </p>
                <div className="space-y-1">
                  {alteracoes.map((alt, idx) => (
                    <div key={idx} className="text-sm flex items-center gap-2">
                      <span className="text-slate-600">{alt.label}:</span>
                      <span className="font-mono text-slate-500">{alt.original}</span>
                      <ArrowRight className="w-3 h-3 text-slate-400" />
                      <span className="font-mono font-medium">{alt.novo}</span>
                      <Badge variant="outline" className="text-xs">{alt.diff}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Motivo do ajuste */}
            <div>
              <Label className="text-sm font-medium">Motivo do Ajuste *</Label>
              <Textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Descreva o motivo do ajuste (mínimo 5 caracteres)..."
                className="mt-1 min-h-[80px]"
              />
              <p className="text-xs text-slate-400 mt-1">{motivo.length} caracteres</p>
            </div>
            
            {/* Checkbox reprocessar */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="reprocessar"
                checked={reprocessar}
                onCheckedChange={setReprocessar}
              />
              <Label htmlFor="reprocessar" className="text-sm cursor-pointer">
                Reprocessar jornada após salvar
              </Label>
            </div>
          </div>
        )}
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSalvar}
            disabled={saving || !temAlteracoes || motivo.trim().length < 5}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvar Ajustes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalAjusteDia;
